import * as React from "react";
import {withRouter} from "react-router";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import * as _ from "lodash";

import {RouterProps} from "../../../../shared/ts/interfaces/router";
import {url} from "../../../../shared/ts/helpers/routing";
import {PaginationState} from "../../../../shared/ts/helpers/pagination";
import {AppError, RequestState} from "../../../../shared/ts/helpers/util";
import {FormActions} from "../../../../shared/ts/helpers/create_form_actions";
import {fetch, HocFetch} from "../../../../shared/ts/decorators/fetch";
import {HocPagination, pagination} from "../../../../shared/ts/decorators/pagination";
import {HocSort, sort} from "../../../../shared/ts/decorators/sort";
import {PAGE_PARAM, SORT_PARAM} from "../../../../shared/ts/constants/queryParams";
import {Store} from "../../project/reducer";
import {Dispatch} from "../../../../shared/ts/interfaces/dispatch";
import {Dictionary} from "../../../../shared/ts/interfaces/structure";
import {closePanel, openPanel, openTab} from "../../context_panel/actions/context_panel";
import ContentBody from "../../project/components/content/ContentBody";
import CenterBox from "../../shared/layout/CenterBox";
import Icon from "../../shared/icon/Icon";
import Loader from "../../shared/loader/Loader";
import Main from "../../shared/layout/Main";
import {Panel, PanelBody} from "../../shared/panel/Panel";
import {Pagination} from "../../shared/pagination/Pagination";
import Content from "../../project/components/content/Content";
import ContentHeader from "../../project/components/content/ContentHeader";
import ContentHeaderTitle from "../../project/components/content/ContentHeaderTitle";
import ContentHeaderControls from "../../project/components/content/ContentHeaderControls";
import {Button} from "../../shared/button/Button";
import ContextPanel from "../../context_panel/components/ContextPanel";
import {NativeListContextPanelHeader} from "../../advertisments/components/context_panel/NativeListContextPanelHeader";
import ContextPanelBody from "../../context_panel/components/ContextPanelBody";
import {TabsContent} from "../../../../shared/ts/components/tabs/TabsContent";
import {campaignListContextPanelActions} from "../../context_panel/reducers/context_panel";
import {IReel, IReelFilterForm} from "../reducers/reels";
import {invalidateReelList, reelFetch} from "../actions/reels/fetch";
import {ReelList} from "../components/ReelList";
import {ReelListFilterForm} from "../components/context_panel/reel/ReelListFilterForm";
import {filterReelListFormActions} from "../actions/reels/filter_reel_list";
import {reelDelete} from "../actions/reels/delete";

const defaultPage = 1;
const defaultSort = "-create_date";

interface IStateProps {
    natives: IReel[];
    pagination: PaginationState;
    request: RequestState;
    errors: AppError | null;
    contextPanelOpened: boolean;
    openedTabIndex: number;
    listFilterForm: IReelFilterForm;
}

interface IActionsProps {
    listFilterFormActions: FormActions;
    actions: {
        invalidateList: typeof invalidateReelList;
        openPanel: typeof openPanel;
        closePanel: typeof closePanel;
        openTab: typeof openTab;
        reelDelete: typeof reelDelete;
    };
}

interface IOwnProps {
    hoc: HocFetch & HocPagination & HocSort;
    onItemClick: (id: number) => void;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {
    additionalQuery: Record<string, string>;
    addNewItemUrl: string;
}


@pagination(PAGE_PARAM)
@sort(SORT_PARAM)
@fetch((state: Store) => state.video.reel.fetch, fetchActions, validateQuery)
export class ReelListViewC extends React.Component<IProps, {}> {

    /**
     * Lifecycle
     */
    public componentWillUnmount() {
        this.props.actions.invalidateList();
    }

    /**
     * Callback
     */
    private onFilterSubmit = (values: Dictionary<any>) => {
        this.props.hoc.fetch.reset(values);
    };

    private onFilterClear = () => {
        this.props.listFilterFormActions.clear();
        this.props.listFilterFormActions.update({
            name: "",
            display_date_range: {lower: "", upper: ""},
            region: {value: 0}
        });
        this.props.hoc.fetch.reset({});
    };

    private onSortChange = (column: string): void => {
        this.props.hoc.sort.toggle(column, this.props.hoc.pagination.get());
    };

    private onContextPanelTabOpen = (tabId: number): void => {
        this.props.actions.openTab(campaignListContextPanelActions.openTab, tabId);
        this.props.actions.openPanel(campaignListContextPanelActions.openPanel);
    };

    private onContextPanelToggle = (): void => {
        if (this.props.contextPanelOpened) {
            this.props.actions.closePanel(campaignListContextPanelActions.closePanel);
        } else {
            this.props.actions.openPanel(campaignListContextPanelActions.openPanel);
        }
    };

    /**
     * Render
     */
    private renderEmptyList(): JSX.Element {
        return (
            <ContentBody className="df fg-1">
                <CenterBox>
                    <Icon icon="empty-list" className="empty-list-icon" />
                    <div className="tac fs30">Nic tu nie ma, zmień parametry szukania</div>
                </CenterBox>
            </ContentBody>
        );
    }

    private renderLoader(): JSX.Element {
        return (
            <CenterBox className="vhc">
                <Loader size="lg" />
            </CenterBox>
        );
    }

    private renderError(): JSX.Element {
        return (
            <CenterBox>
                <Icon icon="empty-list" className="empty-list-icon" />
                <div className="error-message tac fs30">Popraw błędne parametry szukania</div>
            </CenterBox>
        );
    }

    private renderList(loading: boolean): JSX.Element {
        const {hoc, natives, pagination} = this.props;
        return (
            <ContentBody>
                <Panel color="default" className="psr mb-xl">
                    <PanelBody>
                        <ReelList
                            listItems={natives}
                            onSort={this.onSortChange}
                            sortDirection={hoc.sort.getDirection()}
                            sortName={hoc.sort.getName()}
                            loading={loading}
                            onDeleteClick={this.props.actions.reelDelete}
                            onItemClick={this.props.onItemClick}
                        />
                    </PanelBody>
                </Panel>

                <div className="pagination-holder tac pss b-0">
                    <Pagination
                        totalCount={pagination.total}
                        pageSize={pagination.pageSize}
                        activePage={hoc.pagination.currentPage()}
                        onPageChange={hoc.pagination.set}
                    />
                </div>
            </ContentBody>
        );
    }

    private renderContent(): JSX.Element {
        const {request, natives} = this.props;

        if (request === RequestState.Success && _.isEmpty(natives)) {
            return this.renderEmptyList();
        }

        if (request === RequestState.Success || (request === RequestState.Waiting && !_.isEmpty(natives))) {
            return this.renderList(request === RequestState.Waiting);
        }

        if (request === RequestState.Error) {
            return this.renderError();
        }

        return this.renderLoader();
    }

    public render(): JSX.Element {
        return (
            <Main>
                <Content>
                    <ContentHeader>
                        <ContentHeaderTitle>Lista reels</ContentHeaderTitle>
                        <ContentHeaderControls>
                            <Button color="success" className="ttu" href={url(this.props.addNewItemUrl)} size="sm">
                                Dodaj reels
                            </Button>
                        </ContentHeaderControls>
                    </ContentHeader>
                    {this.renderContent()}
                </Content>

                <ContextPanel opened={this.props.contextPanelOpened}>
                    <NativeListContextPanelHeader
                        opened={this.props.contextPanelOpened}
                        openedTabIndex={this.props.openedTabIndex}
                        onTabOpen={this.onContextPanelTabOpen}
                        onPanelToggle={this.onContextPanelToggle}
                    />

                    <ContextPanelBody className="filters-holder">
                        <TabsContent openedTabIndex={this.props.openedTabIndex}>
                            <ReelListFilterForm
                                onSubmit={this.onFilterSubmit}
                                errors={this.props.errors ? this.props.errors.fieldErrors : null}
                                values={this.props.listFilterForm}
                                updateFormAction={this.props.listFilterFormActions.update}
                                clearForm={this.onFilterClear}
                            />
                        </TabsContent>
                    </ContextPanelBody>
                </ContextPanel>
            </Main>
        );
    }
}

const ConnectedReelListView = connect(mapStateToProps, mapActionsToProps)(ReelListViewC);

export const ReelListView = withRouter(
    (props: RouterProps) => (
        <ConnectedReelListView
            additionalQuery={{}}
            onItemClick={(id: number) => props.router.push(url("app:in:video:reel:edit", {id}))}
            addNewItemUrl="app:in:video:reel:add"
        />
    )
);

function mapStateToProps(store: Store): IStateProps {
    return {
        natives: store.video.reel.natives,
        pagination: store.video.reel.pagination,
        request: store.video.reel.fetch.requestState,
        errors: store.video.reel.fetch.errors,
        contextPanelOpened: store.video.reel.list.contextPanel.opened,
        openedTabIndex: store.video.reel.list.contextPanel.openedTabIndex,
        listFilterForm: store.video.reel.list.filterForm
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        listFilterFormActions: bindActionCreators(filterReelListFormActions, dispatch),
        actions: bindActionCreators({
            invalidateList: invalidateReelList,
            openPanel,
            closePanel,
            openTab,
            reelDelete
        }, dispatch)
    };
}

function fetchActions(dispatch: Dispatch, query: Dictionary<string>): void {
    dispatch(reelFetch(query));
}

function validateQuery(query: Dictionary<string>, props: IProps): Dictionary<string> {
    return {
        [PAGE_PARAM]: query[PAGE_PARAM] || defaultPage.toString(),
        [SORT_PARAM]: query[SORT_PARAM] || defaultSort,
        ...props.additionalQuery
    };
}
